import { Editor } from '@tiptap/core';
import React, { ForwardedRef, forwardRef, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { useApplicationErrorHandling } from 'hooks/useApplicationErrorHandling';
import useClipboard from 'hooks/useClipboard';
import useToaster from 'hooks/useToaster';
import { ApplicationError } from 'types/errorHandling';

import sm from '../styles.module.scss';
import { Label } from '../typing';

type LabelWrapperConstraint =
  | { noLabelWrapper: true; onFocusElement: EmptyArgsVoidFunc }
  | { noLabelWrapper?: false; onFocusElement?: undefined };

export const CommonFieldWrapper = forwardRef<
  HTMLElement | Editor,
  React.PropsWithChildren<
    React.ComponentProps<typeof Label> & {
      fieldName: string;
      errorMessage?: string;
      description?: string;
      id: string;
      readOnly?: boolean;
      disabled?: boolean;
    } & LabelWrapperConstraint
  >
>(function CommonFieldWrapper(
  {
    label,
    required,
    children,
    fieldName,
    errorMessage,
    description,
    noLabelWrapper,
    onFocusElement,
    id,
    disabled,
    readOnly,
  },
  ref
) {
  const { setFocus, getValues, formState } = useFormContext();
  const { disabled: formDisabled } = formState;
  const { handleCopy } = useClipboard();
  const { addToaster } = useToaster();
  const handleAppErr = useApplicationErrorHandling();

  const copyValue = async () => {
    if (!getValues(fieldName)) {
      addToaster({
        message: 'No value to copy',
        type: 'info',
      });
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- getValues is not our code
      await handleCopy(getValues(fieldName));
    } catch (e) {
      const err = ApplicationError.FromError(e, 'Failed to copy value');
      handleAppErr(err);
    }
  };

  const onCopyClick = () => {
    void copyValue();
  };

  const errorDiv = useMemo(() => {
    if (!errorMessage) return undefined;

    return (
      <div
        role="alert"
        className={sm.form_error}
        onClick={() => {
          // if it's a rich text
          if (noLabelWrapper) {
            onFocusElement();
            return;
          }
          // all other types
          setFocus(fieldName);
        }}
      >
        {errorMessage} →
      </div>
    );
  }, [errorMessage, fieldName, noLabelWrapper, onFocusElement, setFocus]);

  const descriptionDiv = useMemo(() => {
    if (!description) return undefined;

    return <div className={sm.form_description}>{description}</div>;
  }, [description]);

  if (noLabelWrapper)
    return (
      <>
        {!!label && (
          <div onClick={onFocusElement}>
            <Label label={label} required={required} />
          </div>
        )}
        {children}
        {errorDiv ?? descriptionDiv}
      </>
    );

  return (
    <div
      className={sm.LabelContainer}
      ref={
        /* we use a generic element type and upcast to not leak impl details */
        ref as ForwardedRef<HTMLDivElement>
      }
    >
      <label htmlFor={id}>
        {!!label && (
          <Label
            label={label}
            required={required}
            readOnly={readOnly}
            disabled={disabled || formDisabled}
            onCopyClick={onCopyClick}
          />
        )}
      </label>
      {children}
      {errorDiv ?? descriptionDiv}
    </div>
  );
});
